/**
 * Pure helpers ported 1:1 from stockweb `app/screen/page.tsx` (and the
 * filter/sort applied by stockweb `app/api/screen/route.ts`). Keep these in
 * sync with the original when stockweb changes — see docs/11_SCREENER_PLAN.md.
 * No server imports here: this module is bundled into the client.
 */
import type { CSSProperties } from "react";
import type { ScanState, StageRow } from "@/lib/screener/types";

// stockweb api/screen/route.ts — state priority used for the server-side sort.
export const STATE_ORDER: Record<ScanState, number> = {
  "BUY-BREAKOUT": 0,
  "BUY-PULLBACK": 1,
  "ARMED-EXT": 2,
  "ARMED-WAIT": 3,
};

// stockweb page.tsx `fmt`
export const fmt = (v: number | null | undefined, d = 2) => (v != null ? Number(v).toFixed(d) : "—");

// stockweb page.tsx `formatMcap`
export const formatMcap = (v: number | null | undefined) => {
  if (v == null) return "—";
  if (v >= 1e12) return (v / 1e12).toFixed(2) + "T";
  if (v >= 1e9) return (v / 1e9).toFixed(1) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  return v.toLocaleString();
};

export const fmtSignedPct = (v: number | null | undefined, d = 1) =>
  v == null || Number.isNaN(v) ? "—" : (v >= 0 ? "+" : "") + v.toFixed(d) + "%";

// stockweb page.tsx `getYtdReturn`
export const getYtdReturn = (close: number | null, startPrice: number | null): number | null => {
  if (close == null || startPrice == null || Number(startPrice) <= 0) return null;
  return ((Number(close) - Number(startPrice)) / Number(startPrice)) * 100;
};

// stockweb page.tsx `getRSColorStyle` — red→green heat cell for RS values.
export const getRSColorStyle = (v: number | null | undefined): CSSProperties => {
  if (v == null) return {};
  const val = Number(v);
  const hue = (val / 100) * 125;
  const isDark = val < 30 || val > 75;
  return {
    backgroundColor: `hsl(${hue}, 65%, 40%)`,
    color: isDark ? "#ffffff" : "#0f172a",
    fontWeight: 600,
  };
};

export type MacdStatus = "Above & Bullish" | "Above & Bearish" | "Under & Bullish" | "Under & Bearish";

// stockweb page.tsx `getMacdStatus` (label only; classes live in the cell renderer).
export const getMacdStatus = (
  line: number | null | undefined,
  signal: number | null | undefined,
): MacdStatus | null => {
  if (line == null || signal == null) return null;
  const isAbove = Number(line) > 0;
  const isBull = Number(line) > Number(signal);
  if (isAbove && isBull) return "Above & Bullish";
  if (isAbove && !isBull) return "Above & Bearish";
  if (!isAbove && isBull) return "Under & Bullish";
  return "Under & Bearish";
};

// stockweb page.tsx `calculateScore` — Stage 2 / Stage 1 quality score (0 for 3 & 4).
export function calculateScore(r: StageRow): number {
  const price = Number(r.close);
  const stage = Number(r.stage_status);
  const rs = Number(r.rs_rating ?? 0);
  const sma10 = r.sma_10w ? Number(r.sma_10w) : 0;
  const sma20 = r.sma_20w ? Number(r.sma_20w) : 0;
  const sma30 = r.sma_30w ? Number(r.sma_30w) : 0;
  const vol = r.avg_vol_ratio_9d ? Number(r.avg_vol_ratio_9d) : 0;
  const secRs = Number(r.rs_rating_sector ?? 0);
  const indRs = Number(r.rs_rating_industry ?? 0);

  if (stage === 2) {
    let score = 0;
    // 1. RS Rating (max 35) - continuous scaling
    if (rs >= 60) score += 10 + ((rs - 60) / 40) * 25;
    else if (rs > 0) score += (rs / 60) * 10;

    // 2. MA stack (max 15)
    if (price > sma10) score += 5;
    if (sma10 > sma20) score += 5;
    if (sma20 > sma30) score += 5;

    // Extension (max 15) - curve peaking between 5-15%
    const vsSma30 = sma30 > 0 ? ((price - sma30) / sma30) * 100 : 0;
    let extScore = 0;
    if (vsSma30 >= 0) {
      if (vsSma30 < 10) extScore = 5 + (vsSma30 / 10) * 10;
      else if (vsSma30 <= 15) extScore = 15;
      else extScore = Math.max(0, 15 - ((vsSma30 - 15) / 15) * 15);
    }
    score += extScore;

    // 3. Vol ratio (max 20) - continuous from 0.5 to 1.5
    score += Math.min(20, Math.max(0, ((vol - 0.5) / 1.0) * 20));

    // 4. Sector/Industry RS (max 15)
    score += (secRs / 100) * 7.5;
    score += (indRs / 100) * 7.5;
    return Math.round(score);
  }

  if (stage === 1) {
    let score = 0;
    // 1. Tightness (max 40)
    const diffPct = sma30 > 0 ? Math.abs((sma10 - sma30) / sma30) * 100 : 999;
    if (diffPct <= 5) score += 40 - (diffPct / 5) * 10;
    else score += Math.max(0, 30 - ((diffPct - 5) / 10) * 30);

    // 2. RS Rating (max 30)
    score += (rs / 100) * 30;

    // 3. Vol dry-up (max 30)
    let volScore = 0;
    if (vol > 0) {
      if (vol <= 0.6) volScore = 30;
      else if (vol <= 1.5) volScore = 30 - ((vol - 0.6) / 0.9) * 25;
      else volScore = 5;
    }
    score += volScore;
    return Math.round(score);
  }

  return 0; // Stage 3 & 4
}

// stockweb page.tsx `matchNumberFilter` — reused by the filter builder for ≥ / ≤ / between.
export type NumberFilterMode = "All" | "Above" | "Under" | "Between";
export const matchNumberFilter = (
  val: number | null | undefined,
  mode: NumberFilterMode,
  val1: string,
  val2: string,
): boolean => {
  if (mode === "All") return true;
  if (val == null) return false;
  const v1 = parseFloat(val1);
  const v2 = parseFloat(val2);
  if (mode === "Above") return isNaN(v1) || val >= v1;
  if (mode === "Under") return isNaN(v1) || val <= v1;
  if (mode === "Between") {
    const minVal = isNaN(v1) ? -Infinity : v1;
    const maxVal = isNaN(v2) ? Infinity : v2;
    return val >= minVal && val <= maxVal;
  }
  return true;
};

// stockweb page.tsx `matchMcapFilter` + the <select> options it is driven by.
export const MCAP_OPTIONS = [
  { value: "All", label: "All Market Caps" },
  { value: "Mega-Cap", label: "Mega-Cap (≥ $200B)" },
  { value: "Large-Cap", label: "Large-Cap ($10B - $200B)" },
  { value: "Mid-Cap", label: "Mid-Cap ($2B - $10B)" },
  { value: "Small-Cap", label: "Small-Cap ($300M - $2B)" },
  { value: "Large-Up", label: "Large ขึ้นไป (≥ $10B)" },
  { value: "Mid-Up", label: "Mid ขึ้นไป (≥ $2B)" },
  { value: "Small-Up", label: "Small ขึ้นไป (≥ $300M)" },
  { value: "2B-200B", label: "2B - 200B" },
] as const;

export const matchMcapFilter = (mcap: number | null | undefined, filter: string): boolean => {
  if (filter === "All") return true;
  if (mcap == null) return false;
  switch (filter) {
    case "Mega-Cap":
      return mcap >= 200_000_000_000;
    case "Large-Cap":
      return mcap >= 10_000_000_000 && mcap < 200_000_000_000;
    case "Mid-Cap":
      return mcap >= 2_000_000_000 && mcap < 10_000_000_000;
    case "Small-Cap":
      return mcap >= 300_000_000 && mcap < 2_000_000_000;
    case "Large-Up":
      return mcap >= 10_000_000_000;
    case "Mid-Up":
      return mcap >= 2_000_000_000;
    case "Small-Up":
      return mcap >= 300_000_000;
    case "2B-200B":
      return mcap >= 2_000_000_000 && mcap <= 200_000_000_000;
    default:
      return true;
  }
};

/** Distinct non-empty values, sorted — used for sector/quadrant selects and enum filter options. */
export function distinct(values: (string | null | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v))).sort();
}
